// src/pages/doctorPages/Overviews/Overviews.jsx

import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Button, Checkbox, Modal, message } from "antd";

import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import DoctorScrollTabs from "../../components/ScrollTabs/DoctorScrollTabs";

import useDoctorPatientData from "@/Profile/hooks/useDoctorPatientData";

import { selectSurgicalData } from "../../../Profile/redux/selectors/surgicalHistorySelectors";
import { selectVitalSignals } from "../../../Profile/redux/selectors/vitalSignsSelectors";
import { selectMedicalData } from "../../../Profile/redux/selectors/medicalSelectors";
import { selectPersonalHistoryItems } from "../../../Profile/redux/selectors/personalHistorySelectors";
import { selectVaccinationData } from "../../../Profile/redux/selectors/vaccinationSelectors";
import { selectBmiHistoryData } from "../../../Profile/redux/selectors/bmiHistorySelectors";
import {
  buildAllergyMutation,
  buildSurgeryMutation,
  buildVaccinationMutation,
  executeOverviewMutation,
} from "../../../Profile/functions/doctorOverviewMutations";

import MetricCard from "@/Profile/components/OverViewsPage/MetricCard/MetricCard";
import MetricCarousel from "@/Profile/components/OverViewsPage/MetricCarousel/MetricCarousel";
import BMIChart from "@/Profile/components/OverViewsPage/BMIChart/BMIChart";
import Allergies from "@/Profile/components/OverViewsPage/Allergies/Allergies";
import AllergiesStatus from "@/Profile/components/OverViewsPage/AllergiesStatus/AllergiesStatus";
import VaccinationHistory from "@/Profile/components/OverViewsPage/VaccinationHistory/VaccinationHistory";
import SurgicalHistory from "@/Profile/components/OverViewsPage/SurgicalHistory/SurgicalHistory";
import UpcomingMedicine from "@/Profile/components/OverViewsPage/UpcomingMedicine/UpcomingMedicine";
import OverviewTabsChart from "@/Profile/components/OverViewsPage/OverviewTabsChart/OverviewTabsChartDoctor";

import HeartIcon from "../../../assets/icons/Icon-heart.png";
import BPIcon from "../../../assets/icons/Icon-Blood.png";
import RespiratoryIcon from "../../../assets/icons/Icon-Resperatory.png";
import LeftArrowIcon from "../../../assets/icons/arrowgauche.png";
import { DOCTOR_ROUTES } from "../../../routes/patientRoutes/routePaths";
import { MEDICAL_PATHS } from "../../../api/endpoints";
import { axiosMedical } from "../../../api/axios";
import useMedicalCapabilities, {
  DEFAULT_ACCESS_SCOPES,
  MEDICAL_ACCESS_SCOPE_OPTIONS,
} from "../../../authorization/useMedicalCapabilities";
import { axiosRDV } from "../../../api/axios";

import "../Overviews/Overviews.css";

// ✅ Supprime les .00 inutiles du backend : "22.00" → "22", "22.50" → "22.5"
const cleanVal = (val) => {
  if (val === null || val === undefined) return undefined;
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : val;
};

const firstMeasurement = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return cleanVal(value);
    }
  }
  return null;
};

const normalizeMetricLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const RESERVED_CUSTOM_METRIC_LABELS = new Set([
  "glycemie",
  "glucose",
  "blood glucose",
  "tension",
  "tension arterielle",
  "blood pressure",
  "pression arterielle",
  "pouls",
  "frequence cardiaque",
  "heart rate",
  "frequence respiratoire",
  "respiratory rate",
  "temperature",
  "spo2",
  "saturation",
  "saturation oxygene",
  "oxygen saturation",
  "poids",
  "weight",
]);

const CUSTOM_METRIC_TONES = ["low", "success", "danger", "warning", "neutral"];

const metricTimestamp = (signal) => {
  const timestamp = new Date(
    signal?.measuredAt ||
      signal?.updatedAt ||
      signal?.createdAt ||
      signal?.addedAt ||
      signal?.date ||
      0,
  ).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const lifestyleValueForApi = (type, value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const normalized = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (normalized.includes("occasion")) return "Occasionally";
  if (type === "SMOKING" && (normalized.includes("ex") || normalized.includes("ancien"))) {
    return "Occasionally";
  }
  if (normalized.includes("week") || normalized.includes("hebdo")) return "Weekly";
  if (
    normalized.includes("daily") ||
    normalized.includes("quotid") ||
    normalized.includes("regular") ||
    normalized.includes("regulier") ||
    (type === "SMOKING" &&
      (normalized.includes("active") || normalized.includes("actif") || normalized.includes("current")))
  ) {
    return "Daily";
  }
  if (
    normalized.includes("never") ||
    normalized.includes("jamais") ||
    normalized.includes("none") ||
    normalized.includes("aucun") ||
    normalized.includes("non")
  ) {
    return "Never";
  }
  return text;
};

const mapLifestyleToOverview = (medical, t) => {
  const mapValue = (value) => {
    if (!value || !String(value).trim()) return null;
    const v = String(value).toLowerCase();

    if (v === "never")        return { label: t("doctorOverviews.lifestyle.never"),        tone: "success" };
    if (v === "occasionally") return { label: t("doctorOverviews.lifestyle.occasionally"), tone: "warning" };
    if (v === "weekly")       return { label: t("doctorOverviews.lifestyle.weekly"),       tone: "warning" };
    if (v === "daily")        return { label: t("doctorOverviews.lifestyle.daily"),        tone: "danger"  };

    return { label: value, tone: "neutral" };
  };

  return {
    smoking: mapValue(medical?.tabac),
    alcohol: mapValue(medical?.alcool),
    sport:   mapValue(medical?.activitePhysique),
  };
};

function DoctorOverviewCard({ sizeClass, children }) {
  const contentRef = useRef(null);
  const [rowSpan, setRowSpan] = useState(1);

  useLayoutEffect(() => {
    const content = contentRef.current;
    const grid = content?.parentElement;
    if (!content || !grid) return undefined;

    const updateRowSpan = () => {
      const height = content.getBoundingClientRect().height;
      const gap =
        Number.parseFloat(
          window.getComputedStyle(grid).getPropertyValue("--ov-gap"),
        ) || 0;
      setRowSpan(Math.max(1, Math.ceil(height + gap)));
    };

    updateRowSpan();
    const observer = new ResizeObserver(updateRowSpan);
    observer.observe(content);
    return () => observer.disconnect();
  }, [sizeClass]);

  return (
    <div
      className={`sortable-item ${sizeClass}`}
      style={{ gridRowEnd: `span ${rowSpan}` }}
    >
      <div className="card-wrapper" ref={contentRef}>
        {children}
      </div>
    </div>
  );
}

export default function DoctorOverviewsPage() {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const localPatientData =
    location.state?.patientData || location.state?.patient || null;
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const subprofileId = String(
    searchParams.get("subprofileId") ||
      localPatientData?.subprofileId ||
      localPatientData?.subProfileId ||
      "",
  ).trim() || null;
  const accessPatientId = String(
    searchParams.get("patientId") ||
      localPatientData?.principalPatientId ||
      localPatientData?.raw?.patientId ||
      localPatientData?.linkedUserId ||
      (subprofileId && String(patientId) !== subprofileId ? patientId : "") ||
      patientId ||
      "",
  ).trim();
  const doctorUser = useSelector((state) => state.doctor?.user || null);
  const doctorId =
    doctorUser?.id ||
    doctorUser?.doctorId ||
    doctorUser?.pgId ||
    doctorUser?._id ||
    null;
  const {
    capabilities,
    status: accessStatus,
    loading: capabilitiesLoading,
    refresh: refreshCapabilities,
  } = useMedicalCapabilities(accessPatientId, doctorId, subprofileId);
  const canReadMedicalInformation = Boolean(
    accessStatus === "local-owner" ||
      capabilities?.medicalInformation?.read,
  );
  const canEditMedicalInformation = Boolean(
    accessStatus === "local-owner" ||
      (capabilities?.medicalInformation?.create &&
        capabilities?.medicalInformation?.update),
  );

  // Lecture Redux disponible avant les callbacks de mutation/version.
  const medical = useSelector(selectMedicalData);
  const surgicalDataFromRedux = useSelector(selectSurgicalData);
  const personalHistoryItems = useSelector(selectPersonalHistoryItems);
  const vaccinationData = useSelector(selectVaccinationData);
  const vitalSignals = useSelector(selectVitalSignals);
  const bmiHistory = useSelector(selectBmiHistoryData);
  const customMetrics = useMemo(() => {
    if (!Array.isArray(vitalSignals)) return [];

    const groups = new Map();
    vitalSignals
      .filter(
        (signal) =>
          String(signal?.type || "").toUpperCase() === "OTHER" &&
          !RESERVED_CUSTOM_METRIC_LABELS.has(
            normalizeMetricLabel(signal?.customLabel),
          ),
      )
      .forEach((signal) => {
        const label = String(signal?.customLabel || "Metric").trim();
        const unit = String(signal?.unit || "").trim();
        const key = `${label}__${unit}`;
        if (!groups.has(key)) {
          groups.set(key, {
            id: `custom-${encodeURIComponent(key)}`,
            label,
            unit,
            signals: [],
          });
        }
        groups.get(key).signals.push(signal);
      });

    return Array.from(groups.values()).map((group, index) => {
      const latest = [...group.signals].sort(
        (a, b) => metricTimestamp(b) - metricTimestamp(a),
      )[0];
      return {
        ...group,
        value: cleanVal(latest?.value) ?? latest?.value ?? "--",
        tone: CUSTOM_METRIC_TONES[index % CUSTOM_METRIC_TONES.length],
      };
    });
  }, [vitalSignals]);

  const latestBmi = useMemo(() => {
    if (!Array.isArray(bmiHistory) || bmiHistory.length === 0) return null;
    return [...bmiHistory].sort(
      (a, b) =>
        new Date(b?.date || b?.createdAt || 0).getTime() -
        new Date(a?.date || a?.createdAt || 0).getTime(),
    )[0];
  }, [bmiHistory]);

  const latestLifestyleValues = useMemo(() => {
    const latestByType = (types) =>
      [...(Array.isArray(personalHistoryItems) ? personalHistoryItems : [])]
        .filter((item) => types.includes(String(item?.type || "").toUpperCase()))
        .sort(
          (a, b) =>
            new Date(b?.createdAt || b?.updatedAt || 0).getTime() -
            new Date(a?.createdAt || a?.updatedAt || 0).getTime(),
        )[0]?.value;

    return {
      smoking: latestByType(["SMOKING"]),
      alcohol: latestByType(["ALCOHOL"]),
      sport: latestByType(["SPORT", "PHYSICAL_ACTIVITY"]),
    };
  }, [personalHistoryItems]);

  // ✅ État d'édition
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [accessRequestSent, setAccessRequestSent] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [requestedAccessScopes, setRequestedAccessScopes] = useState(DEFAULT_ACCESS_SCOPES);

  useEffect(() => {
    setAccessRequestSent(false);
  }, [accessPatientId, subprofileId]);

  const sourceMedical = localPatientData
    ? {
        ...medical,
        ...Object.fromEntries(
          Object.entries(localPatientData).filter(
            ([, value]) => value !== undefined && value !== null && value !== "",
          ),
        ),
      }
    : medical;

  const localAge = useMemo(() => {
    if (localPatientData?.age !== undefined && localPatientData?.age !== null) {
      return Number(localPatientData.age);
    }
    const value = localPatientData?.dateOfBirth || localPatientData?.birthdate || localPatientData?.birthDate;
    if (!value) return null;
    const birth = new Date(value);
    if (Number.isNaN(birth.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age -= 1;
    return age >= 0 ? age : null;
  }, [localPatientData]);

  const requestMedicalAccess = useCallback(async () => {
    if (requestingAccess) return;
    if (!accessPatientId) {
      message.error(
        t("doctorOverviews.access.requestMissingPatient", {
          defaultValue: "Unable to identify the patient for this request.",
        }),
      );
      return;
    }
    setRequestingAccess(true);
    try {
      await axiosRDV.post("/access-requests", {
        patientId: accessPatientId,
        ...(subprofileId ? { subprofileId } : {}),
        scopes: requestedAccessScopes,
      }, {
        sanitizeRequest: true,
        allowedRequestFields: ["patientId"],
      });
      setAccessRequestSent(true);
      setAccessDialogOpen(false);
      message.success(
        t("doctorOverviews.access.requestSent", {
          defaultValue: "Access request sent to the patient.",
        }),
      );
      void refreshCapabilities();
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          t("doctorOverviews.access.requestFailed", {
            defaultValue: "Unable to request access.",
          }),
      );
    } finally {
      setRequestingAccess(false);
    }
  }, [accessPatientId, refreshCapabilities, requestedAccessScopes, requestingAccess, subprofileId, t]);

  // ✅ Charge toutes les données du patient dans le contexte doctor
  const { refresh: refreshPatientData } = useDoctorPatientData(
    patientId,
    doctorId,
    !capabilitiesLoading && canReadMedicalInformation,
  );

  // ✅ Handlers pour l'édition
  const handleEditMode = () => {
    if (!canEditMedicalInformation) return;
    setIsEditing(true);
    setEditedData({
      height:
        firstMeasurement(latestBmi, ["tailleCm", "heightCm", "taille", "height"]) ??
        firstMeasurement(sourceMedical, ["tailleCm", "heightCm", "taille", "height"]),
      weight:
        firstMeasurement(latestBmi, ["poidsKg", "weightKg", "poids", "weight"]) ??
        firstMeasurement(sourceMedical, ["poidsKg", "weightKg", "poids", "weight"]),
      glucose: latestVitals?.glucose,
      heartRate: latestVitals?.heartRate,
      bloodPressure: latestVitals?.bloodPressure,
      respiratoryRate: latestVitals?.respiratoryRate,
      smoking: latestLifestyleValues.smoking ?? medical?.tabac,
      alcohol: latestLifestyleValues.alcohol ?? medical?.alcool,
      sport: latestLifestyleValues.sport ?? medical?.activitePhysique,
      allergies: allergiesFromRedux,
      surgeries: surgeriesOnly,
      vaccinations: vaccinations,
    });
  };

  useEffect(() => {
    if (location.state?.startEditing && canEditMedicalInformation && !isEditing) {
      handleEditMode();
      navigate(location.pathname, { replace: true, state: { patientData: localPatientData } });
    }
  }, [canEditMedicalInformation, location.pathname, location.state, localPatientData, navigate, isEditing]);

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({});
  };

  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      message.loading({
        content: t("common.saving", { defaultValue: "Enregistrement en cours..." }),
        key: "save_message",
      });

      const savePromises = [];
      const savedSummary = []; // ✅ Track what was saved

      // ✅ Sauvegarder les données médicales (height, weight, lifestyle)
      if (
        editedData.height !== undefined ||
        editedData.weight !== undefined ||
        editedData.smoking !== undefined ||
        editedData.alcohol !== undefined ||
        editedData.sport !== undefined
      ) {
        const bmiPayload = {};
        if (editedData.height !== undefined) {
          bmiPayload.tailleCm = Number(editedData.height);
          savedSummary.push(`📏 Height: ${editedData.height} cm`);
        }
        if (editedData.weight !== undefined) {
          bmiPayload.poidsKg = Number(editedData.weight);
          savedSummary.push(`⚖️ Weight: ${editedData.weight} kg`);
        }
        if (
          editedData.smoking !== undefined &&
          lifestyleValueForApi("SMOKING", editedData.smoking) !==
            lifestyleValueForApi(
              "SMOKING",
              latestLifestyleValues.smoking ?? medical?.tabac,
            )
        ) {
          savePromises.push(
            axiosMedical.post(MEDICAL_PATHS.PERSONAL_HISTORY(patientId), {
              type: "SMOKING",
              value: lifestyleValueForApi("SMOKING", editedData.smoking),
            }),
          );
          savedSummary.push(`🚬 Smoking: ${editedData.smoking}`);
        }
        if (
          editedData.alcohol !== undefined &&
          lifestyleValueForApi("ALCOHOL", editedData.alcohol) !==
            lifestyleValueForApi(
              "ALCOHOL",
              latestLifestyleValues.alcohol ?? medical?.alcool,
            )
        ) {
          savePromises.push(
            axiosMedical.post(MEDICAL_PATHS.PERSONAL_HISTORY(patientId), {
              type: "ALCOHOL",
              value: lifestyleValueForApi("ALCOHOL", editedData.alcohol),
            }),
          );
          savedSummary.push(`🍷 Alcohol: ${editedData.alcohol}`);
        }
        if (
          editedData.sport !== undefined &&
          lifestyleValueForApi("SPORT", editedData.sport) !==
            lifestyleValueForApi(
              "SPORT",
              latestLifestyleValues.sport ?? medical?.activitePhysique,
            )
        ) {
          savePromises.push(
            axiosMedical.post(MEDICAL_PATHS.PERSONAL_HISTORY(patientId), {
              type: "SPORT",
              value: lifestyleValueForApi("SPORT", editedData.sport),
            }),
          );
          savedSummary.push(`⛹️ Sport: ${editedData.sport}`);
        }

        if (
          Number.isFinite(bmiPayload.tailleCm) &&
          bmiPayload.tailleCm > 0 &&
          Number.isFinite(bmiPayload.poidsKg) &&
          bmiPayload.poidsKg > 0 &&
          (bmiPayload.tailleCm !== Number(latestBmi?.tailleCm) ||
            bmiPayload.poidsKg !== Number(latestBmi?.poidsKg))
        ) {
          savePromises.push(
            axiosMedical.post(MEDICAL_PATHS.BMI_HISTORY(patientId), bmiPayload),
          );
        }
      }

      // ✅ Sauvegarder les signes vitaux (glucose, heartRate, bloodPressure, respiratoryRate)
      if (
        editedData.glucose !== undefined ||
        editedData.heartRate !== undefined ||
        editedData.bloodPressure !== undefined ||
        editedData.respiratoryRate !== undefined
      ) {
        const vitalSignalsPayload = [];

        if (editedData.glucose !== undefined) {
          vitalSignalsPayload.push({
            type: "GLYCEMIA",
            value: editedData.glucose,
            measuredAt: new Date().toISOString(),
          });
          savedSummary.push(`🩸 Blood Glucose: ${editedData.glucose} mg/dL`);
        }
        if (editedData.heartRate !== undefined) {
          vitalSignalsPayload.push({
            type: "HEART_RATE",
            value: editedData.heartRate,
            measuredAt: new Date().toISOString(),
          });
          savedSummary.push(`❤️ Heart Rate: ${editedData.heartRate} bpm`);
        }
        if (editedData.bloodPressure !== undefined) {
          // Parse blood pressure if it's a string like "120/80"
          const bpParts = String(editedData.bloodPressure).split("/");
          if (bpParts.length === 2) {
            savePromises.push(
              axiosMedical.post(
                MEDICAL_PATHS.VITALS(patientId),
                {
                  type: "BP_SYS",
                  value: bpParts[0],
                  measuredAt: new Date().toISOString(),
                }
              )
            );
            savePromises.push(
              axiosMedical.post(
                MEDICAL_PATHS.VITALS(patientId),
                {
                  type: "BP_DIA",
                  value: bpParts[1],
                  measuredAt: new Date().toISOString(),
                }
              )
            );
            savedSummary.push(`🩹 Blood Pressure: ${editedData.bloodPressure} mmHg`);
          }
        }
        if (editedData.respiratoryRate !== undefined) {
          vitalSignalsPayload.push({
            type: "RESP_RATE",
            value: editedData.respiratoryRate,
            measuredAt: new Date().toISOString(),
          });
          savedSummary.push(`💨 Respiratory Rate: ${editedData.respiratoryRate} bpm`);
        }

        vitalSignalsPayload.forEach((vital) => {
          savePromises.push(
            axiosMedical.post(
              MEDICAL_PATHS.VITALS(patientId),
              vital
            )
          );
        });
      }

      // ✅ Sauvegarder les allergies
      if (editedData.allergies && Array.isArray(editedData.allergies)) {
        editedData.allergies.forEach((allergy) => {
          if (!String(allergy.label ?? "").trim()) return;
          savePromises.push(
            executeOverviewMutation(
              axiosMedical,
              buildAllergyMutation(patientId, allergy),
            ),
          );
        });
        if (editedData.allergies.length > 0) {
          savedSummary.push(`🧬 Allergies: ${editedData.allergies.map(a => a.label).join(", ")}`);
        }
      }

      // ✅ Sauvegarder les vaccinations
      if (editedData.vaccinations && Array.isArray(editedData.vaccinations)) {
        editedData.vaccinations.forEach((vaccination) => {
          if (String(vaccination.label ?? "").trim()) {
            savePromises.push(
              executeOverviewMutation(
                axiosMedical,
                buildVaccinationMutation(patientId, vaccination),
              ),
            );
          }
        });
        if (editedData.vaccinations.length > 0) {
          savedSummary.push(`💉 Vaccinations: ${editedData.vaccinations.length} updated`);
        }
      }

      // ✅ Sauvegarder les chirurgies
      if (editedData.surgeries && Array.isArray(editedData.surgeries)) {
        editedData.surgeries.forEach((surgery) => {
          if (String(surgery.label ?? "").trim()) {
            savePromises.push(
              executeOverviewMutation(
                axiosMedical,
                buildSurgeryMutation(patientId, surgery),
              ),
            );
          }
        });
        if (editedData.surgeries.length > 0) {
          savedSummary.push(`🏥 Surgeries: ${editedData.surgeries.length} updated`);
        }
      }

      // ✅ Exécuter toutes les promesses en parallèle
      const saveResults = await Promise.allSettled(savePromises);
      await refreshPatientData();

      if (typeof window !== "undefined") {
        const detail = { userId: String(patientId), updatedAt: Date.now() };
        window.dispatchEvent(
          new CustomEvent("patient-medical-measurements-updated", { detail }),
        );
        window.localStorage.setItem(
          "patient-medical-measurements-updated",
          JSON.stringify(detail),
        );
      }

      const failedSave = saveResults.find(
        (result) => result.status === "rejected",
      );
      if (failedSave?.status === "rejected") {
        throw failedSave.reason;
      }

      // ✅ NOUVEAU: Notification détaillée avec toutes les valeurs sauvegardées
      const successMsg = savedSummary.length > 0 
        ? savedSummary.join("\n")
        : "✅ Data saved successfully";

      message.success({
        content: (
          <div>
            <strong>✅ Patient Metrics Saved!</strong>
            <div style={{ marginTop: "8px", fontSize: "12px", whiteSpace: "pre-line" }}>
              {successMsg}
            </div>
          </div>
        ),
        key: "save_message",
        duration: 5,
      });

      setIsEditing(false);
      setEditedData({});
      setIsSaving(false);

      // ✅ Les données vont se rafraîchir via Redux quand le backend notifie les changements
    } catch (error) {
      setIsSaving(false);

      message.error({
        content: (
          <div>
            <strong>❌ Save Failed</strong>
            <div style={{ marginTop: "8px", fontSize: "12px" }}>
              {error?.response?.data?.message || error?.message || "Unable to save data"}
            </div>
          </div>
        ),
        key: "save_message",
        duration: 5,
      });
      if (error?.response?.status === 403) refreshCapabilities();
    }
  }, [
    editedData,
    latestBmi,
    latestLifestyleValues,
    medical,
    patientId,
    refreshCapabilities,
    refreshPatientData,
    t,
  ]);

  const handleDataChange = (field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ─── Métriques (height / weight) ───────────────────────────────────────────
  const metrics = useMemo(() => ({
    height: isEditing && editedData.height !== undefined
      ? editedData.height
      : firstMeasurement(latestBmi, ["tailleCm", "heightCm", "taille", "height"]) ??
        firstMeasurement(sourceMedical, ["tailleCm", "heightCm", "taille", "height"]),
    weight: isEditing && editedData.weight !== undefined
      ? editedData.weight
      : firstMeasurement(latestBmi, ["poidsKg", "weightKg", "poids", "weight"]) ??
        firstMeasurement(sourceMedical, ["poidsKg", "weightKg", "poids", "weight"]),
      }), [sourceMedical, latestBmi, isEditing, editedData]);

  // ─── IMC ───────────────────────────────────────────────────────────────────
  const bmi = useMemo(() => {
    const height =
      firstMeasurement(latestBmi, ["tailleCm", "heightCm", "taille", "height"]) ??
      firstMeasurement(sourceMedical, ["tailleCm", "heightCm", "taille", "height"]);
    const weight =
      firstMeasurement(latestBmi, ["poidsKg", "weightKg", "poids", "weight"]) ??
      firstMeasurement(sourceMedical, ["poidsKg", "weightKg", "poids", "weight"]);
    if (height && weight && height > 0) {
      const h = height / 100;
      return (weight / (h * h)).toFixed(1);
    }
    return null;
  }, [sourceMedical, latestBmi]);

  // ─── Vaccinations ──────────────────────────────────────────────────────────
  const vaccinations = useMemo(() => {
    if (isEditing && editedData.vaccinations) return editedData.vaccinations;
    if (!Array.isArray(vaccinationData)) return [];
    return vaccinationData
      .map((v) => ({
        id: v?.id ?? v?._id,
        backendId: v?.backendId ?? v?.id ?? v?._id,
        label: v?.vaccine ?? v?.label ?? v?.name,
        date: v?.date,
        version: v?.version,
      }))
      .filter((x) => x.label);
  }, [vaccinationData, isEditing, editedData.vaccinations]);

  // ─── Lifestyle ─────────────────────────────────────────────────────────────
  const lifestyleOverview = useMemo(() => {
    const latestByType = (type) => [...personalHistoryItems]
      .filter((item) => item?.type === type)
      .sort((a, b) => +new Date(b?.createdAt ?? 0) - +new Date(a?.createdAt ?? 0))[0]?.value;
    const medicalData = isEditing
      ? { tabac: editedData.smoking, alcool: editedData.alcohol, activitePhysique: editedData.sport }
      : {
          ...medical,
          tabac: latestByType("SMOKING") ?? medical?.tabac,
          alcool: latestByType("ALCOHOL") ?? medical?.alcool,
          activitePhysique:
            latestByType("PHYSICAL_ACTIVITY") ??
            latestByType("SPORT") ??
            medical?.activitePhysique,
        };
    return mapLifestyleToOverview(medicalData, t);
  }, [medical, personalHistoryItems, t, isEditing, editedData.smoking, editedData.alcohol, editedData.sport]);

  // ─── Allergies ─────────────────────────────────────────────────────────────
  const allergiesFromRedux = useMemo(() => {
    if (isEditing && editedData.allergies) return editedData.allergies;
    if (!Array.isArray(surgicalDataFromRedux)) return [];
    return surgicalDataFromRedux
      .filter((item) => item?.type === "ALLERGY")
      .map((a) => ({ label: a?.item || "", severity: a?.reaction || "", id: a?.id, version: a?.version }))
      .filter((a) => !!a.label);
  }, [surgicalDataFromRedux, isEditing, editedData.allergies]);

  // ─── Chirurgies ────────────────────────────────────────────────────────────
  const surgeriesOnly = useMemo(() => {
    if (isEditing && editedData.surgeries) return editedData.surgeries;
    if (!Array.isArray(surgicalDataFromRedux)) return [];
    return surgicalDataFromRedux
      .filter((item) => item?.type === "SURGERY")
      .map((s) => ({
        id:    s?.id,
        backendId: s?.backendId ?? s?.id,
        label: s?.item,
        year:  s?.date ? new Date(s.date).getFullYear() : "",
        version: s?.version,
      }))
      .filter((x) => x.label);
  }, [surgicalDataFromRedux, isEditing, editedData.surgeries]);

  // ─── Derniers signes vitaux avec cleanVal ──────────────────────────────────
  const latestVitals = useMemo(() => {
    if (isEditing && Object.keys(editedData).length > 0) {
      return {
        glucose: editedData.glucose !== undefined ? cleanVal(editedData.glucose) : undefined,
        heartRate: editedData.heartRate !== undefined ? cleanVal(editedData.heartRate) : undefined,
        respiratoryRate: editedData.respiratoryRate !== undefined ? cleanVal(editedData.respiratoryRate) : undefined,
        bloodPressure: editedData.bloodPressure !== undefined ? editedData.bloodPressure : undefined,
      };
    }

    if (!Array.isArray(vitalSignals)) return {};

    const byType = {};
    const dateOf = (s) =>
      new Date(s?.measuredAt || s?.createdAt || s?.addedAt || s?.date || 0).getTime();

    vitalSignals.forEach((s) => {
      if (!s?.type) return;
      const type = s.type;
      if (!byType[type] || dateOf(s) > dateOf(byType[type])) byType[type] = s;
    });

    // ✅ cleanVal sur sys/dia → évite "120.00/80.00"
    const sysCleaned = cleanVal(byType.BP_SYS?.value);
    const diaCleaned = cleanVal(byType.BP_DIA?.value);
    const bpString =
      sysCleaned && diaCleaned ? `${sysCleaned}/${diaCleaned}`
      : sysCleaned              ? `${sysCleaned}`
      : diaCleaned              ? `${diaCleaned}`
      : undefined;

    return {
      // ✅ cleanVal sur chaque valeur — supprime les .00 du backend SQL
      glucose:         cleanVal(byType.GLYCEMIA?.value      ?? byType.BLOOD_GLUCOSE?.value),
      heartRate:       cleanVal(byType.HEART_RATE?.value),
      respiratoryRate: cleanVal(byType.RESP_RATE?.value     ?? byType.RESPIRATORY_RATE?.value),
      bloodPressure:   bpString,
    };
  }, [vitalSignals, isEditing, editedData]);

  // ─── Ordre des cartes ──────────────────────────────────────────────────────
  const metricCardIds = useMemo(
    () => [
      "metric-gly",
      "metric-hr",
      "metric-bp",
      "metric-rr",
      ...customMetrics.map((metric) => metric.id),
    ],
    [customMetrics],
  );

  const cards = [
    "bmi-chart",
    "allergies",
    "surgical-history",
    "upcoming-medicine",
    "overview-tabs-chart",
    "allergies-status",
    "vaccination-history",
  ];

  const getCardSize = (cardId) => {
    if (cardId.startsWith("metric-"))     return "small";
    if (cardId === "bmi-chart")           return "bmi-wide";
    if (cardId === "overview-tabs-chart") return "health-wide";
    if (cardId === "upcoming-medicine")   return "medicine-tall";
    if (cardId === "allergies")           return "medium";
    if (cardId === "surgical-history")    return "medium";
    if (cardId === "allergies-status")    return "small-card";
    if (cardId === "vaccination-history") return "small-card";
    return "medium";
  };

  const renderCard = (cardId) => {
    switch (cardId) {

      // ✅ tone="success" — identique au patient
      case "metric-gly":
        return (
          <MetricCard
            value={latestVitals.glucose ?? "--"}
            unit={t("doctorOverviews.units.mgdl")}
            label={t("doctorOverviews.metrics.bloodGlucose")}
            status={t("doctorOverviews.metrics.normal")}
            tone="success"
            editable={isEditing}
            clickToEdit
            onEditStart={() => {
              if (!isEditing) handleEditMode();
            }}
            onEditEnd={() => {
              if (isEditing) handleSave();
            }}
            onValueChange={(value) => handleDataChange("glucose", value)}
          />
        );

      // ✅ tone="danger" — identique au patient
      case "metric-hr":
        return (
          <MetricCard
            value={latestVitals.heartRate ?? "--"}
            unit={t("doctorOverviews.units.bpm")}
            label={t("doctorOverviews.metrics.heartRate")}
            status={t("doctorOverviews.metrics.normal")}
            tone="danger"
            iconsrc={HeartIcon}
            editable={isEditing}
            clickToEdit
            onEditStart={() => {
              if (!isEditing) handleEditMode();
            }}
            onEditEnd={() => {
              if (isEditing) handleSave();
            }}
            onValueChange={(value) => handleDataChange("heartRate", value)}
          />
        );

      // ✅ tone="low" — identique au patient
      case "metric-bp":
        return (
          <MetricCard
            value={latestVitals.bloodPressure ?? "--"}
            unit={t("doctorOverviews.units.mmhg")}
            label={t("doctorOverviews.metrics.bloodPressure")}
            status={t("doctorOverviews.metrics.normal")}
            tone="low"
            iconsrc={BPIcon}
            editable={isEditing}
            clickToEdit
            onEditStart={() => {
              if (!isEditing) handleEditMode();
            }}
            onEditEnd={() => {
              if (isEditing) handleSave();
            }}
            onValueChange={(value) => handleDataChange("bloodPressure", value)}
          />
        );

      // ✅ tone="warning" — identique au patient
      case "metric-rr":
        return (
          <MetricCard
            value={latestVitals.respiratoryRate ?? "--"}
            unit={t("doctorOverviews.units.bpm")}
            label={t("doctorOverviews.metrics.respiratoryRate")}
            status={t("doctorOverviews.metrics.normal")}
            tone="warning"
            iconsrc={RespiratoryIcon}
            editable={isEditing}
            clickToEdit
            onEditStart={() => {
              if (!isEditing) handleEditMode();
            }}
            onEditEnd={() => {
              if (isEditing) handleSave();
            }}
            onValueChange={(value) => handleDataChange("respiratoryRate", value)}
          />
        );

      case "bmi-chart":
        return (
          <BMIChart
            userId={patientId}
            bmiData={null}
            currentBMI={bmi}
            loading={false}
            error={false}
            height={metrics?.height}
            weight={metrics?.weight}
            editable={isEditing}
            onSave={(height, weight) => {
              handleDataChange("height", height);
              handleDataChange("weight", weight);
            }}
            onCancel={handleCancel}
            saving={false}
          />
        );

      case "allergies":
        return (
          <Allergies
            editable={isEditing}
            initialAllergies={isEditing && editedData.allergies ? editedData.allergies : allergiesFromRedux}
            onAllergiesChange={(allergies) => handleDataChange("allergies", allergies)}
          />
        );

      case "surgical-history":
        return (
          <SurgicalHistory
            editable={isEditing}
            items={isEditing && editedData.surgeries ? editedData.surgeries : surgeriesOnly}
            variant="overview"
            onChange={(surgeries) => handleDataChange("surgeries", surgeries)}
          />
        );

      case "upcoming-medicine":
        return <UpcomingMedicine patientId={patientId} />;

      case "overview-tabs-chart":
        return <OverviewTabsChart patientId={patientId} />;

      case "allergies-status":
        return <AllergiesStatus value={lifestyleOverview} editable={isEditing} onChange={(lifestyle) => {
          handleDataChange("smoking", lifestyle?.smoking?.label);
          handleDataChange("alcohol", lifestyle?.alcohol?.label);
          handleDataChange("sport", lifestyle?.sport?.label);
        }} />;

      case "vaccination-history":
        return (
          <VaccinationHistory
            editable={isEditing}
            initialItems={isEditing && editedData.vaccinations ? editedData.vaccinations : vaccinations}
            onChange={(vaccinations) => handleDataChange("vaccinations", vaccinations)}
          />
        );

      default:
        if (cardId.startsWith("custom-")) {
          const customMetric = customMetrics.find(
            (metric) => metric.id === cardId,
          );
          if (!customMetric) return null;

          return (
            <MetricCard
              value={customMetric.value}
              unit={customMetric.unit}
              label={customMetric.label}
              status={t("doctorOverviews.metrics.custom", {
                defaultValue: "Custom",
              })}
              tone={customMetric.tone}
            />
          );
        }
        return null;
    }
  };

  // ─── Guard patientId ───────────────────────────────────────────────────────
  if (!patientId) {
    return (
      <AppLayoutDoctor>
        <div style={{ padding: 20 }}>
          <p style={{ margin: 0 }}>{t("doctorOverviews.errors.missingPatientId")}</p>
        </div>
      </AppLayoutDoctor>
    );
  }

  return (
    <AppLayoutDoctor>
      <div className="profile doctor-medical-overview-page">
        <DoctorScrollTabs patientId={patientId} />

        <div className="ov-grid">
          <div className="ov-content">
            <div className="ov-top-bar">
              <div className="ov-top-bar-left">
                <button
                  className="ov-back-btn"
                  onClick={() => navigate(DOCTOR_ROUTES.FILE_PAGE(patientId))}
                  title={t("common.goBack", { defaultValue: "Go Back" })}
                >
                  <img src={LeftArrowIcon} alt="back" className="ov-back-icon" />
                </button>
                <div className="ov-title-block">
                  <h2 className="ov-title">{t("doctorOverviews.header.title")}</h2>
                  <p className="ov-subtitle">
                    {t("doctorOverviews.header.subtitle")}
                    {canReadMedicalInformation && localAge !== null
                      ? ` · ${t("doctorOverviews.header.age", { defaultValue: "Age" })}: ${localAge}`
                      : ""}
                  </p>
                </div>
              </div>
              <div className="ov-action-buttons">
                {isEditing ? (
                  <>
                    <button
                      className="ov-cancel-btn"
                      type="button"
                      onClick={handleCancel}
                      disabled={isSaving}
                    >
                      {t("common.cancel", { defaultValue: "Annuler" })}
                    </button>
                    <button
                      className="ov-save-btn"
                      type="button"
                      onClick={handleSave}
                      disabled={isSaving}
                    >
                      {isSaving
                        ? t("common.saving", { defaultValue: "Enregistrement…" })
                        : t("common.save", { defaultValue: "Enregistrer" })}
                    </button>
                  </>
                ) : (
                  <button
                    className="ph-btn--primary"
                    type="button"
                    onClick={handleEditMode}
                    disabled={capabilitiesLoading || !canEditMedicalInformation}
                    title={
                      canEditMedicalInformation
                        ? t("medicalOverview.actions.modifyOverview", {
                            defaultValue: "Modify Overview",
                          })
                        : "Cette vue combine ajout et modification : les deux droits sont requis"
                    }
                  >
                    {t("medicalOverview.actions.modifyOverview", {
                      defaultValue: "Modify Overview",
                    })}
                  </button>
                )}
              </div>
            </div>

            {!capabilitiesLoading &&
              !canEditMedicalInformation &&
              !accessRequestSent &&
              accessStatus !== "pending" && (
              <Alert
                type="info"
                showIcon
                message={t("doctorOverviews.access.title", {
                  defaultValue: "Patient access is required to edit this record",
                })}
                description={t("doctorOverviews.access.description", {
                  defaultValue:
                    "Request permission to modify medical information, add medical records, and manage medications. The patient must approve the request.",
                })}
                action={
                  <Button
                    size="small"
                    loading={requestingAccess}
                    onClick={requestMedicalAccess}
                  >
                    {t("doctorOverviews.access.request", {
                      defaultValue: "Request access",
                    })}
                  </Button>
                }
                style={{ marginBottom: 16 }}
              />
            )}

            {!capabilitiesLoading && canReadMedicalInformation && (
              <>
                <MetricCarousel
                  cards={metricCardIds}
                  renderCard={renderCard}
                  showAddButton={false}
                  className="mc-section--read-only"
                />
                <div className="ov-flexible-grid">
                  {cards.map((cardId) => (
                    <DoctorOverviewCard
                      key={cardId}
                      sizeClass={`card-${getCardSize(cardId)}`}
                    >
                      {renderCard(cardId)}
                    </DoctorOverviewCard>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AppLayoutDoctor>
  );
}
